// Phase 0 / B0 deliverable 5 — the single canonical keyset-pagination hook
// ([D6]). Every P1+ list screen imports `useKeysetQuery`; no batch mints a
// second keyset helper.
//
// Backend contract (BUILD_PLAN §5 "List endpoints"; services/platform-graph
// repo/keyset.ts): list endpoints page on `(created_at, id)` and return
// `{ items, nextCursor }` — never OFFSET. `nextCursor` is an opaque,
// server-issued token; `null` means the last page. This hook wraps TanStack
// Query's `useInfiniteQuery` so a screen consumes pages without re-deriving
// the cursor plumbing.
//
// GET /v1/employees now serves the SAME keyset shape ({ items, nextCursor })
// as every other list route — the BUILD_PLAN §7 carryover that tracked its
// old OFFSET response ({ employees, total }) is closed (see
// content/employee-list-content.tsx, which consumes it via this hook).

import {
  useInfiniteQuery,
  type QueryKey,
  type UseInfiniteQueryResult,
} from "@tanstack/react-query";

/** The canonical keyset page shape every list endpoint returns. */
export interface KeysetPage<TItem> {
  readonly items: readonly TItem[];
  /** Opaque server-issued cursor for the NEXT page; `null` on the last page. */
  readonly nextCursor: string | null;
}

/** Args for {@link useKeysetQuery}. */
export interface KeysetQueryArgs<TItem> {
  /** Stable query key prefix — the cursor is appended internally per page. */
  readonly queryKey: QueryKey;
  /**
   * Fetch one page given the cursor (`null` on first page). Returns the
   * canonical `{ items, nextCursor }` shape — typically
   * `unwrapEnvelope(platformGraph.GET(..., { params: { query: { cursor } } }))`.
   */
  readonly fetchPage: (cursor: string | null) => Promise<KeysetPage<TItem>>;
  /** Per-query stale window override (ms). Falls back to the client default. */
  readonly staleTime?: number;
  /** Disable the query (e.g. until a route param resolves). */
  readonly enabled?: boolean;
}

/**
 * The flattened result a list screen consumes: all loaded items across
 * pages, plus the infinite-query control surface for "load more".
 */
export interface KeysetQueryResult<TItem> {
  /** All items loaded so far, flattened in page order. */
  readonly items: readonly TItem[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
  readonly fetchNextPage: () => void;
  readonly refetch: () => void;
}

/**
 * Keyset (seek) pagination hook over TanStack Query's `useInfiniteQuery`.
 * The page param IS the opaque cursor; `getNextPageParam` reads `nextCursor`
 * (a `null` cursor stops pagination — `undefined` to TanStack signals "no
 * more pages"). Forbidden: OFFSET/page-number paging on any new path.
 */
export function useKeysetQuery<TItem>(
  args: KeysetQueryArgs<TItem>,
): KeysetQueryResult<TItem> {
  const query: UseInfiniteQueryResult<
    { pages: KeysetPage<TItem>[]; pageParams: (string | null)[] },
    unknown
  > = useInfiniteQuery({
    queryKey: args.queryKey,
    queryFn: ({ pageParam }) => args.fetchPage(pageParam),
    initialPageParam: null as string | null,
    // `null` nextCursor → no more pages. Returning `undefined` is how
    // TanStack Query learns there is no next page.
    getNextPageParam: (lastPage: KeysetPage<TItem>): string | null | undefined =>
      lastPage.nextCursor ?? undefined,
    ...(args.staleTime !== undefined ? { staleTime: args.staleTime } : {}),
    ...(args.enabled !== undefined ? { enabled: args.enabled } : {}),
  });

  const items = (query.data?.pages ?? []).flatMap((page) => page.items);

  return {
    items,
    // NOT `query.isLoading` (= isPending && isFetching). TanStack pauses a
    // query while its online manager reports offline: `fetchStatus: "paused"`,
    // nothing fetched, no error. That combination read as "loaded, and the
    // list is empty", so every consumer of this hook rendered its no-results
    // empty state during a network outage — "Makale bulunamadı" on a help
    // centre whose endpoints were simply unreachable. Pending with nothing
    // loaded is LOADING; only an idle (disabled) query is allowed to fall
    // through to the empty state.
    isLoading: query.isPending && query.fetchStatus !== "idle",
    isError: query.isError,
    error: query.error,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: (): void => {
      void query.fetchNextPage();
    },
    refetch: (): void => {
      void query.refetch();
    },
  };
}

/** Every row of a bounded window, plus whether the cap cut it short. */
export interface KeysetWindow<TItem> {
  readonly items: readonly TItem[];
  readonly truncated: boolean;
}

/**
 * Drain a server-filtered window page by page up to `cap` rows.
 *
 * For views that must show a WHOLE window at once (a month grid), where a
 * "load more" button would leave days silently empty. The window itself must
 * be narrowed server-side (`from`/`to`); this only follows the cursor. Hitting
 * the cap reports `truncated` only when the server still had more to give.
 */
export async function collectKeysetWindow<TItem>(
  fetchPage: (cursor: string | null) => Promise<KeysetPage<TItem>>,
  cap: number,
): Promise<KeysetWindow<TItem>> {
  const items: TItem[] = [];
  let cursor: string | null = null;
  do {
    const page: KeysetPage<TItem> = await fetchPage(cursor);
    items.push(...page.items);
    cursor = page.nextCursor;
    if (items.length >= cap) {
      return { items: items.slice(0, cap), truncated: cursor !== null || items.length > cap };
    }
  } while (cursor !== null);
  return { items, truncated: false };
}
